import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { FolderTree, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Google Picker integration — required by Google's `drive.file` scope.
 *
 * Loads the picker JS on demand, opens it in modal mode, and returns the
 * selected folder via `onChange({ id, name })`. The picker is configured to
 * show ONLY folders (mimeType=application/vnd.google-apps.folder) so the user
 * picks a directory, not individual files.
 */
export default function GooglePickerButton({ accountId, value, onChange }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [scriptReady, setScriptReady] = useState(false);
  const configRef = useRef(null);

  // Load the Google Picker script once for the whole app.
  useEffect(() => {
    const existing = document.getElementById("google-api-js");
    if (existing) {
      setScriptReady(true);
      return;
    }
    const s = document.createElement("script");
    s.id = "google-api-js";
    s.src = "https://apis.google.com/js/api.js";
    s.async = true;
    s.defer = true;
    s.onload = () => setScriptReady(true);
    document.body.appendChild(s);
  }, []);

  const openPicker = async () => {
    if (!accountId) {
      toast.error(t("picker.noAccount"));
      return;
    }
    if (!scriptReady || !window.gapi) {
      toast.error(t("picker.loading"));
      return;
    }
    setLoading(true);
    try {
      // 1. ALWAYS fetch fresh access_token + dev key from our backend.
      // The OAuth access_token expires in ~1h, and reusing a stale token causes
      // Picker to return 403 silently even after GCP restrictions are fixed.
      const { data } = await api.get(`/drive/accounts/${accountId}/picker-config`);
      configRef.current = data;
      const cfg = configRef.current;
      if (!cfg.developer_key) {
        toast.error(t("picker.noKey"));
        setLoading(false);
        return;
      }

      // 2. Load the picker module (idempotent)
      await new Promise((resolve) => window.gapi.load("picker", { callback: resolve }));

      // 3. Build the picker — folders only, single select
      const view = new window.google.picker.DocsView(
        window.google.picker.ViewId.FOLDERS
      )
        .setSelectFolderEnabled(true)
        .setMimeTypes("application/vnd.google-apps.folder")
        .setIncludeFolders(true)
        .setParent("root");

      const picker = new window.google.picker.PickerBuilder()
        .addView(view)
        .setOAuthToken(cfg.access_token)
        .setDeveloperKey(cfg.developer_key)
        .setAppId(cfg.app_id || undefined)
        .setOrigin(window.location.protocol + "//" + window.location.host)
        .setTitle(t("picker.title"))
        .setCallback((data) => {
          if (data.action === window.google.picker.Action.PICKED) {
            const doc = data.docs?.[0];
            if (doc) onChange({ id: doc.id, name: doc.name });
          }
        })
        .build();
      picker.setVisible(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Picker failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-border bg-card p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center bg-[#002FA7] text-white">
          <FolderTree className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] uppercase text-muted-foreground">
            {t("picker.selectedLabel")}
          </div>
          <div className="truncate text-sm font-medium">
            {value?.name || t("picker.noneSelected")}
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          className="rounded-sm gap-2"
          onClick={openPicker}
          disabled={loading || !accountId}
          data-testid="open-google-picker"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {t("picker.browse")}
        </Button>
      </div>
      <p className="mt-3 font-mono text-[10px] text-muted-foreground">{t("picker.hint")}</p>
    </div>
  );
}
